import type { MouseEvent } from "react";
import type { SideNavMode } from "./SideNav.types";
import {
    longestTransitionMs,
    openWidthAt,
    OVERDRAG_RUBBER_BAND_PX,
    rubberBand,
    RUBBER_BAND_PX,
    type PointerSample,
} from "./sideNavDragMath";
import { fittedOpenWidth, MIN_OPEN_WIDTH, MINI_WIDTH } from "./sideNavWidth";

/** A release that never snapped and never got this far from the press is still a click, so a shaky click toggles. */
const CLICK_TRAVEL_PX = 8;
/** Travel toward the other width (from mini, or past the minimum open width) that snaps to it, during the drag. */
const SNAP_DISTANCE_PX = 48;
/** A mini nav dragged this close to its left edge (half its width) slides off, and hides on release. */
const HIDE_AT_PX = MINI_WIDTH / 2;

export const OTHER_MODE: Record<SideNavMode, SideNavMode> = {
    open: "mini",
    mini: "open",
};
/** Sign of the pointer travel that leads to each width. */
export const DIRECTION_TO: Record<SideNavMode, number> = { open: 1, mini: -1 };

/** A press on the held spot, not on the edge itself, only counts toward a double-click. */
export const isOnHeldSpot = (event: MouseEvent<HTMLElement>) => {
    const handle = event.currentTarget;
    const { left, right } = handle.getBoundingClientRect();
    return (
        handle.hasAttribute("data-press-held") &&
        (event.clientX < left || event.clientX > right)
    );
};

/** One press-to-release on the nav's edge. */
export interface DragSession {
    pointerId: number;
    startMode: SideNavMode;
    mode: SideNavMode;
    startX: number;
    /** The saved open width; the stretch is measured from it while open. */
    openWidth: number;
    /** Where the nav's left edge was at the press, so the pointer's x minus it is the width it points at. */
    edgeX: number;
    /** Where every label fits, measured at the press and never short of the saved width; only an open start resizes. */
    fittedWidth: number;
    /** Crossing it switches between the start mode and the other one. */
    snapX: number;
    /** Unless resizing: where the stretch is zero, the press or the pointer once the latest snap has landed. */
    originX: number;
    /** Left of it, a mini nav slides off. */
    hideX: number;
    isHiding: boolean;
    /** When it last slid off or back in; sliding back keeps the overdrag's origin, unlike a snap. */
    slidAt: number;
    /** The farthest the pointer got from the press. */
    maxTravel: number;
    isDragging: boolean;
    snappedAt: number;
    /** How long a snap animates; after it the nav tracks the pointer 1:1. */
    snapMs: number;
    stretch: number;
    samples: PointerSample[];
}

/**
 * A session for a press at `startX`. Its geometry comes from the saved
 * `width`, not the DOM: an open nav snaps to mini 48px past the minimum
 * width, a mini one snaps open (to the saved width) 48px right of the press.
 */
export const startDragSession = ({
    pointerId,
    mode,
    width,
    startX,
    time,
    nav,
}: {
    pointerId: number;
    mode: SideNavMode;
    width: number;
    startX: number;
    time: number;
    nav: HTMLElement | null;
}): DragSession => {
    const edgeX = startX - (mode === "open" ? width : MINI_WIDTH);
    return {
        pointerId,
        startMode: mode,
        mode,
        startX,
        openWidth: width,
        edgeX,
        fittedWidth:
            mode === "open" && nav !== null
                ? fittedOpenWidth({ nav, width })
                : width,
        snapX:
            mode === "open"
                ? edgeX + MIN_OPEN_WIDTH - SNAP_DISTANCE_PX
                : startX + SNAP_DISTANCE_PX,
        originX: startX,
        hideX: (nav?.getBoundingClientRect().left ?? 0) + HIDE_AT_PX,
        isHiding: false,
        slidAt: Number.NEGATIVE_INFINITY,
        maxTravel: 0,
        isDragging: false,
        snappedAt: Number.NEGATIVE_INFINITY,
        snapMs: longestTransitionMs(nav),
        stretch: 0,
        samples: [{ x: startX, time }],
    };
};

/** A release that neither snapped nor travelled far: the browser's click toggles. */
export const isClickRelease = (session: DragSession) =>
    session.mode === session.startMode && session.maxTravel < CLICK_TRAVEL_PX;

/** A drag that started on the open nav and is still open resizes it; any other snaps. */
export const isResizing = (session: DragSession) =>
    session.startMode === "open" && session.mode === "open";

/**
 * The stretch for the pointer at `x`: while resizing, from the saved open
 * width to the width the pointer implies; otherwise a rubber band from where
 * the latest snap landed.
 */
export const stretchAt = ({
    session,
    x,
    isSnapping,
}: {
    session: DragSession;
    x: number;
    isSnapping: boolean;
}) => {
    if (isResizing(session)) {
        const openWidth = openWidthAt({
            pointerWidth: x - session.edgeX,
            fittedWidth: session.fittedWidth,
        });
        return openWidth - session.openWidth;
    }
    const isTowardOther =
        (x - session.originX) * DIRECTION_TO[OTHER_MODE[session.mode]] > 0;
    // Overdrag waits for the snap to land, so the snap ends on the exact width.
    if (isSnapping && !isTowardOther) {
        session.originX = x;
    }
    return rubberBand({
        distance: x - session.originX,
        limit: isTowardOther ? RUBBER_BAND_PX : OVERDRAG_RUBBER_BAND_PX,
    });
};

/** The nav's stretch: its width grows by it. Open, the page column follows; mini, its inline-end margin shrinks, so the page stays put. */
export const applyStretch = ({
    nav,
    stretch,
    phase,
}: {
    nav: HTMLElement;
    stretch: number;
    /**
     * `snapping` keeps the nav's transitions on while a snap animates;
     * `tracking` follows the pointer 1:1; `hiding` slides it off, still mounted
     * so the drag can bring it back.
     */
    phase: "snapping" | "tracking" | "hiding";
}) => {
    nav.style.setProperty("--side-nav-stretch", String(stretch));
    nav.setAttribute("data-edge-drag", phase);
};

export const clearStretch = (nav: HTMLElement) => {
    nav.style.removeProperty("--side-nav-stretch");
    nav.removeAttribute("data-edge-drag");
};
